'use client';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["className", "render", "start", "end", "addPropertiesToDroppedEvent"];
import * as React from 'react';
import { useStore } from '@base-ui/utils/store';
import { useRenderElement } from "../../base-ui-copy/utils/useRenderElement.mjs";
import { useCompositeListItem } from "../../base-ui-copy/composite/list/useCompositeListItem.mjs";
import { useEventCalendarStoreContext } from "../../use-event-calendar-store-context/index.mjs";
import { useAdapterContext } from "../../use-adapter-context/index.mjs";
import { schedulerNowSelectors } from "../../scheduler-selectors/index.mjs";
import { EVENT_CREATION_PRECISION_MINUTE } from "../../constants/index.mjs";
import { useEventCreation } from "../../internals/utils/useEventCreation.mjs";
import { CalendarGridTimeColumnContext } from "./CalendarGridTimeColumnContext.mjs";
import { useTimeDropTarget } from "./useTimeDropTarget.mjs";
import { jsx as _jsx } from "react/jsx-runtime";
export const CalendarGridTimeColumn = /*#__PURE__*/React.forwardRef(function CalendarGridTimeColumn(componentProps, forwardedRef) {
  const {
      // Rendering props

      // Internal props
      start,
      end,
      addPropertiesToDroppedEvent
      // Props forwarded to the DOM element
    } = componentProps,
    elementProps = _objectWithoutPropertiesLoose(componentProps, _excluded);
  const adapter = useAdapterContext();
  const store = useEventCalendarStoreContext();
  const isCurrentDay = useStore(store, schedulerNowSelectors.isCurrentDay, start);
  const {
    ref: listItemRef,
    index
  } = useCompositeListItem();
  const {
    getCursorPositionInElementMs,
    ref: dropTargetRef
  } = useTimeDropTarget({
    start,
    end,
    addPropertiesToDroppedEvent
  });
  const eventCreationProps = useEventCreation(({
    event,
    creationConfig
  }) => {
    const offsetMs = getCursorPositionInElementMs({
      input: {
        clientY: event.clientY
      },
      elementRef: dropTargetRef
    });
    const anchor = adapter.addMilliseconds(start, offsetMs);
    const startDate = adapter.addMinutes(anchor, -(adapter.getMinutes(anchor) % EVENT_CREATION_PRECISION_MINUTE));
    return {
      surfaceType: 'time-grid',
      start: startDate,
      end: adapter.addMinutes(startDate, creationConfig.duration),
      resourceId: null
    };
  });
  const state = React.useMemo(() => ({
    current: isCurrentDay
  }), [isCurrentDay]);
  const props = {
    role: 'gridcell'
  };
  const contextValue = React.useMemo(() => ({
    start,
    end,
    index,
    getCursorPositionInElementMs
  }), [start, end, index, getCursorPositionInElementMs]);
  const element = useRenderElement('div', componentProps, {
    state,
    ref: [forwardedRef, dropTargetRef, listItemRef],
    props: [props, eventCreationProps, elementProps]
  });
  return /*#__PURE__*/_jsx(CalendarGridTimeColumnContext.Provider, {
    value: contextValue,
    children: element
  });
});
if (process.env.NODE_ENV !== "production") CalendarGridTimeColumn.displayName = "CalendarGridTimeColumn";